import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../../lib/tracker.js';
import '../../styles/gbr-consent.css';

declare global {
  interface Window {
    gbrt?: {
      track: (name: string, props?: Record<string, unknown>) => void;
      consent: (all: boolean) => void;
      status: () => { consent: string; decided: boolean; gpc: boolean; off: boolean };
      reset: () => void;
    };
  }
}

/**
 * Slim analytics consent bar (bottom, never covers content on mobile thanks to
 * body padding). Without a choice we only count anonymously and cookieless;
 * "Accept" allows a first-party cookie so returning visits can be recognised.
 * Shown once; "Essential only" is remembered for a year. Honours GPC.
 */
export function ConsentBanner() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = window.gbrt;
    if (!t) return;
    const sync = () => {
      const s = t.status();
      setOpen(!s.decided && !s.gpc && !s.off);
    };
    sync();
    window.addEventListener('gbr-consent', sync);
    return () => window.removeEventListener('gbr-consent', sync);
  }, []);
  useEffect(() => {
    document.body.classList.toggle('has-consent-bar', open && !pathname.startsWith('/admin'));
    return () => document.body.classList.remove('has-consent-bar');
  }, [open, pathname]);
  if (!open || pathname.startsWith('/admin')) return null;
  const choose = (all: boolean) => {
    window.gbrt?.consent(all);
    setOpen(false);
  };
  return (
    <div className="cb" role="dialog" aria-label="Privacy choice" aria-live="polite">
      <p className="cb-t">
        We count visits anonymously, without cookies, on our own server. May we also set one cookie to recognise you when you come back? No ads, no third parties.{' '}
        <Link to="/privacy#analytics">Details</Link>
      </p>
      <div className="cb-b">
        <button type="button" className="cb-x" onClick={() => choose(false)}>Essential only</button>
        <button type="button" className="cb-ok" onClick={() => choose(true)}>Accept</button>
      </div>
    </div>
  );
}

/** Small control for the privacy page: shows the current choice and lets the visitor change it. */
export function ConsentControl() {
  const [state, setState] = useState<string>('');
  const [gpc, setGpc] = useState(false);
  useEffect(() => {
    const sync = () => {
      const s = window.gbrt?.status();
      setState(s?.consent || '');
      setGpc(!!s?.gpc);
    };
    sync();
    window.addEventListener('gbr-consent', sync);
    return () => window.removeEventListener('gbr-consent', sync);
  }, []);
  const label = gpc ? 'Your browser sends Global Privacy Control, so we only count anonymously.' : state === 'all' ? 'You allowed the recognition cookie.' : state === 'essential' ? 'You chose essential only (anonymous counting).' : 'You have not decided yet (anonymous counting until you do).';
  return (
    <div className="cb-ctl">
      <p>{label}</p>
      {!gpc && (
        <div className="cb-b">
          {state !== 'all' && <button type="button" className="cb-ok" onClick={() => window.gbrt?.consent(true)}>Allow recognition cookie</button>}
          {state !== 'essential' && <button type="button" className="cb-x" onClick={() => window.gbrt?.consent(false)}>Essential only</button>}
        </div>
      )}
    </div>
  );
}
